import { useState, useMemo } from "react";
import { AI_TOOLS } from "@/data/content";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Star, ArrowUpRight, Search as SearchIcon } from "lucide-react";

export default function AITools() {
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("All");
    const categories = ["All", ...Array.from(new Set(AI_TOOLS.map((t) => t.category)))];

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        return AI_TOOLS.filter((t) => {
            const okCat = cat === "All" || t.category === cat;
            const okQ =
                !s ||
                t.name.toLowerCase().includes(s) ||
                t.tagline.toLowerCase().includes(s) ||
                t.category.toLowerCase().includes(s);
            return okCat && okQ;
        });
    }, [q, cat]);

    return (
        <>
            <PageHeader
                eyebrow="AI Tools"
                title="The tools we open every single day."
                subtitle="Every entry is one we personally use or have vetted. Sponsored placements are labelled clearly."
            />
            <section className="container-editorial pb-8">
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                    <div className="relative flex-1 max-w-md">
                        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                        <Input
                            data-testid="ai-tools-search"
                            className="pl-9 h-11"
                            placeholder="Search tools…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {categories.map((c) => (
                            <button
                                key={c}
                                data-testid={`ai-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                                onClick={() => setCat(c)}
                                className={`text-xs uppercase tracking-widest px-3 py-2 rounded-full border transition ${
                                    cat === c
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "bg-white border-stone-300 hover:border-primary/40"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="container-editorial pb-24">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filtered.map((t) => (
                        <a
                            key={t.slug}
                            id={t.slug}
                            href={t.link}
                            data-testid={`ai-tool-${t.slug}`}
                            className="card-editorial rounded-lg p-6 flex flex-col group"
                        >
                            <div className="flex items-start justify-between">
                                <span className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center">
                                    <Sparkles className="h-5 w-5" />
                                </span>
                                <Badge className="bg-stone-100 text-stone-700 hover:bg-stone-100 border-0 text-[10px]">{t.category}</Badge>
                            </div>
                            <h3 className="font-serif text-2xl mt-5">{t.name}</h3>
                            <p className="text-stone-600 text-sm mt-2">{t.tagline}</p>
                            <p className="text-sm text-stone-500 mt-3 flex-1">{t.description}</p>
                            <div className="mt-5 pt-4 border-t border-stone-200 flex items-center justify-between text-sm">
                                <span className="text-stone-500">{t.pricing}</span>
                                <span className="flex items-center gap-1 text-accent font-semibold">
                                    <Star className="h-4 w-4 fill-current" /> {t.rating}
                                </span>
                            </div>
                            <span className="mt-3 text-primary text-sm font-semibold inline-flex items-center gap-1">
                                Visit tool <ArrowUpRight className="h-4 w-4" />
                            </span>
                        </a>
                    ))}
                </div>
                {filtered.length === 0 && (
                    <p className="text-center text-stone-500 py-16" data-testid="ai-tools-empty">No tools match your filters.</p>
                )}
            </section>
        </>
    );
}
